import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import API_URL from '../config/api';
import { 
  FiUsers, 
  FiZap, 
  FiTrendingUp,
  FiCheckCircle,
  FiClock,
  FiBarChart2,
  FiUserPlus,
  FiDownload,
  FiAlertCircle,
  FiBriefcase,
  FiAlertTriangle,
  FiSun,
  FiCloudOff
} from 'react-icons/fi';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalClients: 0,
    activePower: 0,
    withoutPower: 0,
    disconnected: 0,
    urgent: 0,
    forTransfer: 0,
    cancelled: 0,
    recentActivity: [],
    powerStatusDistribution: [],
    upcomingDueDates: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Obtener estadísticas de clientes
      const clientsResponse = await axios.get(`${API_URL}/api/clients?limit=1000`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const clients = clientsResponse.data.clients || [];
      
      // Calcular estadísticas
      const totalClients = clients.length;
      const activePower = clients.filter(c => c.powerStatus === 'activo' && !c.urgent).length;
      const withoutPower = clients.filter(c => c.powerStatus === 'sin_luz' && !c.urgent).length;
      const disconnected = clients.filter(c => c.powerStatus === 'desconectado').length;
      const urgent = clients.filter(c => c.urgent === true).length;
      const forTransfer = clients.filter(c => c.forTransfer === true).length;
      const cancelled = clients.filter(c => c.cancellationReason && c.cancellationReason !== '').length;
      
      // Distribución por estado de luz
      const powerStatusDistribution = [
        { name: 'Activo', value: activePower, color: 'bg-green-500' },
        { name: 'Sin Luz', value: withoutPower, color: 'bg-yellow-500' },
        { name: 'Desconectado', value: disconnected, color: 'bg-gray-500' },
        { name: 'Urgente', value: urgent, color: 'bg-red-500' }
      ].filter(item => item.value > 0);
      
      // Próximos vencimientos (próximos 7 días)
      const today = new Date();
      const nextWeek = new Date(today);
      nextWeek.setDate(today.getDate() + 7);
      
      const upcomingDueDates = clients
        .filter(c => {
          if (!c.dueDate) return false;
          const dueDate = new Date(c.dueDate);
          return dueDate >= today && dueDate <= nextWeek && c.powerStatus === 'activo';
        })
        .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
        .slice(0, 5)
        .map(c => ({
          id: c._id,
          name: c.fullName,
          company: c.company,
          dueDate: c.dueDate,
          clientNumber: c.clientNumber
        }));
      
      // Actividad reciente (simulada con clientes recién creados o modificados)
      const recentActivity = clients
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 5)
        .map(c => ({
          type: c.urgent ? 'urgente' : 
                 c.powerStatus === 'sin_luz' ? 'alerta' : 
                 c.powerStatus === 'desconectado' ? 'desconectado' : 'nuevo',
          description: c.urgent ? `⚠️ Cliente marcado como URGENTE` :
                      c.powerStatus === 'sin_luz' ? `💡 Cliente sin luz` :
                      c.powerStatus === 'desconectado' ? `🔌 Cliente desconectado` :
                      `📝 Nuevo cliente registrado`,
          time: formatTimeAgo(new Date(c.createdAt)),
          client: c.fullName,
          company: c.company
        }));
      
      setStats({
        totalClients,
        activePower,
        withoutPower,
        disconnected,
        urgent,
        forTransfer,
        cancelled,
        recentActivity,
        powerStatusDistribution,
        upcomingDueDates
      });
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('No se pudieron cargar los datos del dashboard');
      
      // Datos de ejemplo para desarrollo
      setStats({
        totalClients: 156,
        activePower: 98,
        withoutPower: 23,
        disconnected: 15,
        urgent: 8,
        forTransfer: 12,
        cancelled: 5,
        powerStatusDistribution: [
          { name: 'Activo', value: 98, color: 'bg-green-500' },
          { name: 'Sin Luz', value: 23, color: 'bg-yellow-500' },
          { name: 'Desconectado', value: 15, color: 'bg-gray-500' },
          { name: 'Urgente', value: 8, color: 'bg-red-500' }
        ],
        upcomingDueDates: [
          { id: 1, name: 'Juan Pérez', company: 'Empresa A', dueDate: new Date(Date.now() + 2*24*60*60*1000).toISOString() },
          { id: 2, name: 'María García', company: 'Empresa B', dueDate: new Date(Date.now() + 3*24*60*60*1000).toISOString() },
          { id: 3, name: 'Carlos López', company: 'Empresa C', dueDate: new Date(Date.now() + 5*24*60*60*1000).toISOString() }
        ],
        recentActivity: [
          { type: 'urgente', description: '⚠️ Cliente marcado como URGENTE', time: 'Hace 10 minutos', client: 'Juan Pérez', company: 'Empresa A' },
          { type: 'alerta', description: '💡 Cliente sin luz', time: 'Hace 2 horas', client: 'María García', company: 'Empresa B' },
          { type: 'nuevo', description: '📝 Nuevo cliente registrado', time: 'Hace 5 horas', client: 'Carlos López', company: 'Empresa C' }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins} minutos`;
    if (diffHours < 24) return `Hace ${diffHours} horas`;
    return `Hace ${diffDays} días`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const StatCard = ({ title, value, icon: Icon, color, bgColor, trend, subtitle }) => (
    <div className="bg-white rounded-lg shadow p-4 md:p-6 hover:shadow-lg transition-all transform hover:scale-105">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs md:text-sm text-gray-600 mb-1 truncate">{title}</p>
          <p className="text-xl md:text-2xl font-bold truncate">{value}</p>
          {trend && (
            <p className="text-xs text-green-600 mt-2 flex items-center">
              <FiTrendingUp className="mr-1 flex-shrink-0" />
              <span className="truncate">{trend}</span>
            </p>
          )}
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1 truncate">{subtitle}</p>
          )}
        </div>
        <div className={`${bgColor} p-3 md:p-4 rounded-full ml-2 flex-shrink-0`}>
          <Icon className={`text-xl md:text-2xl ${color}`} />
        </div>
      </div>
    </div>
  );

  const handleNavigate = (path) => {
    navigate(path);
  };

  const handleGenerateReport = () => {
    // Navegar a la página de reportes
    navigate('/reports');
    toast.success('Generando reporte...');
  };

  const handleImportClients = () => {
    // Navegar a la página de importación
    navigate('/clients/import');
  };

  const handleManageUrgents = () => {
    // Navegar a clientes con filtro de urgentes
    navigate('/clients?filter=urgent');
    toast.info('Mostrando clientes urgentes');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
        <div className="flex">
          <FiAlertCircle className="text-red-400 mr-3" size={20} />
          <div>
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={fetchDashboardData}
              className="text-sm text-red-600 hover:text-red-800 mt-2 underline"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 p-4 md:p-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Dashboard</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">
            Bienvenido de nuevo, {user?.name || 'Usuario'}
          </p>
        </div>
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-3 py-2 md:px-4 md:py-2 rounded-lg shadow-md text-sm md:text-base whitespace-nowrap">
          <FiZap className="inline mr-2" />
          Flex Energy - {new Date().toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
        </div>
      </div>

      {/* Stats Grid - Adaptado a los nuevos estados */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        <StatCard
          title="Total Clientes"
          value={stats.totalClients.toLocaleString()}
          icon={FiUsers}
          color="text-blue-600"
          bgColor="bg-blue-100"
          trend="+12 este mes"
        />
        
        <StatCard
          title="Con Luz (Activo)"
          value={stats.activePower.toLocaleString()}
          icon={FiSun}
          color="text-green-600"
          bgColor="bg-green-100"
          subtitle={`${Math.round((stats.activePower / stats.totalClients) * 100)}% del total`}
        />
        
        <StatCard
          title="Sin Luz / Urgente"
          value={(stats.withoutPower + stats.urgent).toLocaleString()}
          icon={FiCloudOff}
          color="text-yellow-600"
          bgColor="bg-yellow-100"
          subtitle={`${stats.urgent} urgentes, ${stats.withoutPower} sin luz`}
        />
        
        <StatCard
          title="Desconectados"
          value={stats.disconnected.toLocaleString()}
          icon={FiAlertTriangle}
          color="text-gray-600"
          bgColor="bg-gray-100"
          subtitle={`${stats.forTransfer} para traspaso`}
        />
      </div>

      {/* Segunda fila de estadísticas */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Para Traspaso</h3>
            <FiBriefcase className="text-blue-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.forTransfer}</p>
          <p className="text-xs text-gray-500 mt-1">Clientes pendientes de traspaso</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Cancelados</h3>
            <FiAlertCircle className="text-red-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.cancelled}</p>
          <p className="text-xs text-gray-500 mt-1">Por falta de pago u otros motivos</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Tasa de servicio</h3>
            <FiCheckCircle className="text-green-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {Math.round(((stats.activePower + stats.withoutPower) / stats.totalClients) * 100)}%
          </p>
          <p className="text-xs text-gray-500 mt-1">Clientes con servicio activo</p>
        </div>
      </div>

      {/* Charts Row - Adaptado */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Distribución por Estado de Luz */}
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <h2 className="text-base md:text-lg font-semibold mb-4">Estado de la Luz</h2>
          <div className="space-y-3">
            {stats.powerStatusDistribution?.length > 0 ? (
              stats.powerStatusDistribution.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-2 md:p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center min-w-0">
                    <div className={`w-2 h-2 md:w-3 md:h-3 rounded-full mr-2 md:mr-3 flex-shrink-0 ${item.color}`}></div>
                    <span className="text-sm md:text-base truncate">{item.name}</span>
                  </div>
                  <span className="text-sm md:text-base font-bold ml-2">{item.value}</span>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No hay datos de estados</p>
            )}
          </div>
        </div>

        {/* Próximos vencimientos */}
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <h2 className="text-base md:text-lg font-semibold mb-4">Próximos Vencimientos</h2>
          <div className="space-y-3">
            {stats.upcomingDueDates?.length > 0 ? (
              stats.upcomingDueDates.map((client, index) => {
                const daysLeft = Math.ceil((new Date(client.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
                return (
                  <div key={index} className="flex items-center justify-between p-2 md:p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{client.name}</p>
                      <p className="text-xs text-gray-600 truncate">{client.company}</p>
                    </div>
                    <div className="text-right ml-2">
                      <p className="text-sm font-bold text-yellow-700">{formatDate(client.dueDate)}</p>
                      <p className="text-xs text-yellow-600">
                        {daysLeft === 0 ? 'Hoy' : daysLeft === 1 ? 'Mañana' : `${daysLeft} días`}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-center py-4">No hay vencimientos próximos</p>
            )}
          </div>
        </div>
      </div>

      {/* Actividad Reciente */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 md:px-6 py-4 border-b flex justify-between items-center">
          <h2 className="text-base md:text-lg font-semibold">Actividad Reciente</h2>
          <span 
            onClick={() => navigate('/notifications')}
            className="text-xs md:text-sm text-blue-600 cursor-pointer hover:underline"
          >
            Ver todo
          </span>
        </div>
        <div className="divide-y">
          {stats.recentActivity?.length > 0 ? (
            stats.recentActivity.map((activity, index) => (
              <div
                key={index}
                className="px-4 md:px-6 py-3 md:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    activity.type === 'urgente' ? 'bg-red-500' :
                    activity.type === 'alerta' ? 'bg-yellow-500' :
                    activity.type === 'desconectado' ? 'bg-gray-500' : 'bg-green-500'
                  }`}></div>
                  <div className="min-w-0">
                    <p className="text-xs md:text-sm font-medium text-gray-900 truncate">
                      {activity.description}
                    </p>
                    <p className="text-xs text-gray-500">
                      {activity.client} {activity.company && `· ${activity.company}`}
                    </p>
                  </div>
                </div>
                <span className="text-xs md:text-sm text-gray-500 mt-1 sm:mt-0 ml-5 sm:ml-0">
                  {activity.time}
                </span>
              </div>
            ))
          ) : (
            <div className="px-4 md:px-6 py-8 text-center text-gray-500">
              <p>No hay actividad reciente</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions - Botones Funcionales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        <button
          onClick={handleImportClients}
          className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 md:p-6 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all transform hover:scale-105 shadow-lg cursor-pointer"
        >
          <FiUserPlus className="text-2xl md:text-3xl mb-2 md:mb-3 mx-auto" />
          <span className="block font-semibold text-sm md:text-base">Importar Clientes</span>
          <span className="text-xs opacity-90 mt-1 block hidden sm:block">Desde Excel o texto</span>
        </button>

        <button
          onClick={handleManageUrgents}
          className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white p-4 md:p-6 rounded-lg hover:from-yellow-600 hover:to-yellow-700 transition-all transform hover:scale-105 shadow-lg cursor-pointer"
        >
          <FiAlertCircle className="text-2xl md:text-3xl mb-2 md:mb-3 mx-auto" />
          <span className="block font-semibold text-sm md:text-base">Gestionar Urgentes</span>
          <span className="text-xs opacity-90 mt-1 block hidden sm:block">{stats.urgent} clientes urgentes</span>
        </button>

        <button
          onClick={handleGenerateReport}
          className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 md:p-6 rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg cursor-pointer sm:col-span-2 lg:col-span-1"
        >
          <FiDownload className="text-2xl md:text-3xl mb-2 md:mb-3 mx-auto" />
          <span className="block font-semibold text-sm md:text-base">Generar Reporte</span>
          <span className="text-xs opacity-90 mt-1 block hidden sm:block">Exportar datos</span>
        </button>
      </div>
    </div>
  );
};

export default Dashboard;